import { useState } from "react";
import {
  Check,
  Gift,
  Leaf,
  Shield,
  Droplets,
  Bone,
  MessageCircle,
  Phone,
  PackageOpen,
  Cable,
  ShoppingBag,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BookingDialog from "../components/BookingDialog";
import { Toaster } from "../components/ui/sonner";
import { useScrollReveal } from "../lib/useScrollReveal";
import { BRAND } from "../lib/brand";

const WA_LINK = "https://wa.me/919867413477";
const TEL_LINK = "tel:+919867413477";

const PRODUCT_IMAGES = {
  navy:
    "https://customer-assets.emergentagent.com/job_luxury-yoga-studio/artifacts/z2ubm4eg_IMG_20260527_082110271.jpg",
  wine:
    "https://customer-assets.emergentagent.com/job_luxury-yoga-studio/artifacts/re7x4izp_IMG_20260519_165749344.jpg",
  olive:
    "https://customer-assets.emergentagent.com/job_luxury-yoga-studio/artifacts/n54v8yjv_IMG_20260527_082608780.jpg",
};

const BADGES = [
  "Eco-Friendly",
  "Anti-Slip",
  "Waterproof",
  "Joint Safe",
  "Instructor Tested",
];

const FEATURES = [
  "Eco-Friendly TPE Material",
  "Anti-Slip Surface",
  "Waterproof & Easy to Clean",
  "Joint Safe — 6mm Cushioning",
  "Instructor Tested & Approved",
];

const COLOURS = [
  {
    name: "Navy Blue",
    swatch: "#1E2D4A",
    image: PRODUCT_IMAGES.navy,
  },
  {
    name: "Wine",
    swatch: "#A24A5C",
    image: PRODUCT_IMAGES.wine,
  },
  {
    name: "Olive Green",
    swatch: "#6E7A4A",
    image: PRODUCT_IMAGES.olive,
  },
];

/* ---------- Page Hero ---------- */
const PageHero = () => {
  const ref = useScrollReveal();
  return (
    <section
      ref={ref}
      data-testid="products-hero"
      className="relative pt-36 pb-20 md:pt-44 md:pb-28 bg-gradient-to-b from-[#F1ECE0] via-[#F9F6F0] to-[#F9F6F0]"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
        <p className="reveal text-[11px] tracking-[0.35em] uppercase text-[#D4A24C] mb-5 font-medium">
          Our Products
        </p>
        <h1 className="reveal font-serif-display text-5xl sm:text-6xl md:text-7xl text-[#153326] font-light leading-[1.05]">
          Gear That <em className="italic">Moves</em> With You
        </h1>
        <p className="reveal mt-6 text-[#2C5F4A]/80 text-base md:text-lg font-light max-w-xl mx-auto">
          Crafted for serious practitioners. Built to last. Backed by instructors.
        </p>
      </div>
    </section>
  );
};

/* ---------- Section 1: Product Intro ---------- */
const ProductIntro = () => {
  const ref = useScrollReveal();
  return (
    <section
      ref={ref}
      data-testid="product-intro"
      className="py-20 md:py-28 bg-[#F9F6F0]"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
        <p className="reveal text-[11px] tracking-[0.35em] uppercase text-[#D4A24C] mb-5 font-medium">
          Yogiinit Signature Collection
        </p>
        <h2 className="reveal font-serif-display text-4xl sm:text-5xl md:text-6xl text-[#153326] font-light leading-[1.05]">
          The Mat That <em className="italic">Started</em> It All
        </h2>
        <p className="reveal mt-6 text-[#2C5F4A]/85 leading-relaxed text-base md:text-lg font-light max-w-2xl mx-auto">
          Our TPE 6mm Yoga Mat is instructor-tested, and built for every body —
          beginner to advanced. Available in 3 colours, each mat comes with a free
          strap and bag.
        </p>
        <div className="reveal mt-8 flex flex-wrap justify-center gap-2">
          {BADGES.map((b) => (
            <span
              key={b}
              className="text-[11px] tracking-wide px-3 py-1.5 rounded-full bg-[#1F4A38]/6 text-[#2C5F4A] border border-[#1F4A38]/15"
            >
              {b}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Section 2: Product Cards ---------- */
const ProductCards = () => {
  const ref = useScrollReveal();
  return (
    <section
      ref={ref}
      data-testid="product-cards"
      className="py-20 md:py-28 bg-gradient-to-b from-[#F9F6F0] to-[#F1ECE0]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14 md:mb-16">
          <p className="reveal text-[11px] tracking-[0.35em] uppercase text-[#D4A24C] mb-5 font-medium">
            Three Variants
          </p>
          <h2 className="reveal font-serif-display text-4xl sm:text-5xl md:text-6xl text-[#153326] font-light">
            Choose Your <em className="italic">Colour</em>
          </h2>
          <p className="reveal mt-5 text-[#2C5F4A]/80 font-light">
            Same premium quality. Three distinct expressions.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7 items-stretch">
          {COLOURS.map((c, i) => (
            <article
              key={c.name}
              data-testid={`product-card-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="reveal group rounded-[28px] bg-white/75 backdrop-blur-xl border border-[#1F4A38]/10 shadow-[0_12px_40px_rgba(31,74,56,0.08)] hover:shadow-[0_22px_60px_rgba(31,74,56,0.14)] hover:-translate-y-2 transition-all duration-500 ease-out flex flex-col overflow-hidden"
            >
              {/* Image */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={c.image}
                  alt={`Yogiinit TPE 6mm Yoga Mat — ${c.name}`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute top-4 left-4 text-[10px] tracking-[0.3em] uppercase bg-white/90 backdrop-blur text-[#1F4A38] rounded-full px-3 py-1 font-semibold">
                  TPE 6mm
                </span>
              </div>

              <div className="p-7 md:p-8 flex flex-col flex-1">
                {/* Colour dot + title */}
                <div className="flex items-center gap-3">
                  <span
                    className="h-5 w-5 rounded-full border-2 border-white shadow-[0_0_0_1.5px_rgba(31,74,56,0.18)]"
                    style={{ backgroundColor: c.swatch }}
                  />
                  <h3 className="font-serif-display text-2xl md:text-3xl text-[#153326] font-medium">
                    {c.name}
                  </h3>
                </div>
                <p className="mt-1 text-sm text-[#2C5F4A]/80">TPE 6mm Yoga Mat</p>

                <div className="my-6 h-px bg-[#1F4A38]/10" />

                {/* Features */}
                <ul className="space-y-3">
                  {FEATURES.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <span className="mt-0.5 h-5 w-5 rounded-full bg-[#1F4A38]/10 text-[#1F4A38] flex items-center justify-center shrink-0">
                        <Check size={11} strokeWidth={2.8} />
                      </span>
                      <span className="text-[14px] text-[#153326]">{f}</span>
                    </li>
                  ))}
                </ul>

                {/* Free inclusions */}
                <div className="mt-7 rounded-2xl bg-[#D4A24C]/10 border border-[#D4A24C]/30 p-4">
                  <p className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#A57E2F] font-semibold mb-2">
                    <Gift size={14} strokeWidth={1.8} />
                    Free Inclusions
                  </p>
                  <ul className="text-sm text-[#153326] space-y-1">
                    <li>Free Hanging Mat Strap</li>
                    <li>Free Mat Carry Bag</li>
                  </ul>
                </div>

                {/* Pricing */}
                <div className="mt-7 flex items-end gap-3">
                  <span className="text-base text-[#2C5F4A]/55 line-through">
                    ₹1,500
                  </span>
                  <span className="font-serif-display text-4xl text-[#1F4A38] font-semibold leading-none">
                    ₹1,000
                  </span>
                </div>
                <p className="mt-1 text-xs font-medium tracking-wide text-[#1F4A38]">
                  You save ₹500
                </p>

                {/* CTA */}
                <div className="mt-auto pt-7">
                  <a
                    href={WA_LINK}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`buy-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="block text-center w-full bg-[#1F4A38] hover:bg-[#153326] text-[#F9F6F0] rounded-full px-6 py-3.5 text-sm font-semibold tracking-wide transition-all duration-300 hover:shadow-lg"
                  >
                    Buy Now — WhatsApp Us
                  </a>
                  <p className="mt-3 text-center text-xs text-[#2C5F4A]/70">
                    For orders &amp; enquiries, connect on WhatsApp
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Section 3: Why This Mat ---------- */
const REASONS = [
  {
    Icon: Leaf,
    title: "Eco-Friendly",
    desc: "Made from non-toxic TPE, safe for you and the planet.",
  },
  {
    Icon: Shield,
    title: "Anti-Slip",
    desc: "Firm grip on any surface, so you can focus on your form.",
  },
  {
    Icon: Droplets,
    title: "Waterproof",
    desc: "Wipe clean in seconds. Hygienic and low maintenance.",
  },
  {
    Icon: Bone,
    title: "Joint Safe",
    desc: "6mm cushioning protects knees, wrists and spine.",
  },
];

const WhyMat = () => {
  const ref = useScrollReveal();
  return (
    <section
      ref={ref}
      data-testid="why-mat"
      className="relative overflow-hidden bg-[#1F4A38] text-[#F9F6F0]"
    >
      <div className="grain absolute inset-0" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="reveal text-[11px] tracking-[0.35em] uppercase text-[#D4A24C] mb-5 font-medium">
            Why Yogiinit Mat
          </p>
          <h2 className="reveal font-serif-display text-4xl sm:text-5xl md:text-6xl font-light leading-[1.05]">
            Not Just A Mat. <em className="italic text-[#EAC98A]">A Foundation.</em>
          </h2>
          <p className="reveal mt-6 text-[#F9F6F0]/85 font-light max-w-2xl mx-auto">
            Every Yogiinit mat is personally tested by our instructors before it
            reaches you. We only sell what we practice on.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {REASONS.map(({ Icon, title, desc }) => (
            <div
              key={title}
              className="reveal rounded-[24px] bg-white/5 backdrop-blur-md border border-white/10 p-7 hover:bg-white/10 hover:-translate-y-1 transition-all duration-500"
            >
              <div className="h-12 w-12 rounded-2xl bg-[#D4A24C]/15 text-[#D4A24C] flex items-center justify-center mb-5">
                <Icon size={22} strokeWidth={1.6} />
              </div>
              <h3 className="font-serif-display text-2xl">{title}</h3>
              <p className="mt-2 text-[14px] text-[#F9F6F0]/80 leading-relaxed">
                {desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Section 4: Free Inclusions ---------- */
const INCLUSIONS = [
  {
    Icon: PackageOpen,
    title: "Yogiinit TPE 6mm Mat",
    desc: "Your foundation for every practice.",
  },
  {
    Icon: Cable,
    title: "Free Hanging Mat Strap",
    desc: "Carry with ease, anywhere you go.",
  },
  {
    Icon: ShoppingBag,
    title: "Free Mat Carry Bag",
    desc: "Keep it clean, keep it protected.",
  },
];

const Inclusions = () => {
  const ref = useScrollReveal();
  return (
    <section
      ref={ref}
      data-testid="inclusions"
      className="py-20 md:py-28 bg-[#F9F6F0]"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14 md:mb-16">
          <p className="reveal text-[11px] tracking-[0.35em] uppercase text-[#D4A24C] mb-5 font-medium">
            What's In The Box
          </p>
          <h2 className="reveal font-serif-display text-4xl sm:text-5xl md:text-6xl text-[#153326] font-light">
            Every Mat <em className="italic">Comes</em> With
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7">
          {INCLUSIONS.map(({ Icon, title, desc }) => (
            <div
              key={title}
              className="reveal rounded-[28px] bg-white/75 backdrop-blur-xl border border-[#1F4A38]/10 shadow-[0_10px_36px_rgba(31,74,56,0.06)] hover:shadow-[0_18px_50px_rgba(31,74,56,0.12)] hover:-translate-y-1 transition-all duration-500 p-9 text-center"
            >
              <div className="h-16 w-16 rounded-2xl bg-[#1F4A38]/8 text-[#1F4A38] flex items-center justify-center mx-auto mb-6">
                <Icon size={28} strokeWidth={1.5} />
              </div>
              <h3 className="font-serif-display text-2xl md:text-3xl text-[#153326] font-medium">
                {title}
              </h3>
              <p className="mt-3 text-[#2C5F4A] leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Section 5: Order CTA ---------- */
const OrderCTA = () => {
  const ref = useScrollReveal();
  return (
    <section
      ref={ref}
      data-testid="order-cta"
      className="relative overflow-hidden bg-[#1F4A38] text-[#F9F6F0]"
    >
      <div className="grain absolute inset-0" />
      <div className="relative max-w-4xl mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
        <h2 className="reveal font-serif-display text-5xl sm:text-6xl md:text-7xl leading-[1.05] font-light">
          Ready to <em className="italic text-[#EAC98A]">Begin?</em>
        </h2>
        <p className="reveal mt-7 max-w-xl mx-auto text-[#F9F6F0]/85 font-light text-base md:text-lg">
          Order your Yogiinit Mat today. Limited stock available.
        </p>
        <p className="reveal mt-5 text-[#EAC98A] font-medium tracking-wide">
          <span className="font-serif-display text-2xl">₹1,000</span> only
          <span className="mx-2 text-[#F9F6F0]/50">·</span>
          <span className="text-[#F9F6F0]/75">Market value</span>{" "}
          <span className="line-through text-[#F9F6F0]/60">₹1,500</span>
        </p>

        <div className="reveal mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href={WA_LINK}
            target="_blank"
            rel="noreferrer"
            data-testid="order-whatsapp-btn"
            className="inline-flex items-center justify-center gap-2 bg-[#D4A24C] hover:bg-[#C29344] text-[#153326] rounded-full px-8 py-4 text-sm font-semibold tracking-wide shadow-[0_18px_50px_rgba(212,162,76,0.35)] hover:shadow-[0_24px_60px_rgba(212,162,76,0.55)] transition-all duration-400 hover:-translate-y-0.5"
          >
            <MessageCircle size={18} strokeWidth={1.8} />
            Order on WhatsApp
          </a>
          <a
            href={TEL_LINK}
            data-testid="order-call-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 text-white/95 hover:bg-white hover:text-[#1F4A38] px-8 py-4 text-sm font-medium tracking-wide transition-all duration-400 backdrop-blur-sm"
          >
            <Phone size={18} strokeWidth={1.8} />
            Call to Order
          </a>
        </div>

        <p className="reveal mt-7 text-sm text-[#F9F6F0]/70 tracking-wide">
          {BRAND.contact.phone} · Mon – Sun
        </p>
      </div>
    </section>
  );
};

/* ---------- Page composition ---------- */
const ProductsPage = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  const openBooking = () => setBookingOpen(true);

  return (
    <div className="App">
      <Header onBook={openBooking} />
      <main>
        <PageHero />
        <ProductIntro />
        <ProductCards />
        <WhyMat />
        <Inclusions />
        <OrderCTA />
      </main>
      <Footer />
      <BookingDialog open={bookingOpen} onOpenChange={setBookingOpen} />
      <Toaster position="top-center" richColors />
    </div>
  );
};

export default ProductsPage;
